import { Grid, makeStyles, Typography } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from 'components/Button/Button';
import DateTimePicker from 'components/DateTimePicker/DateTimePicker';
import TextInput from 'components/TextInput/TextInput';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedProject } from 'store/projects/selectors/projectsSelectors';
import { closeAddNewUserDialog } from 'store/projectUsers/actions/project-users.actions';
import { selectAddNewUserDialogMeta } from 'store/projectUsers/selectors/projectUsers.selectors';
import { createOrUpdateUser } from 'store/user/thunks/createOrUpdateUser.thunk';
import { joinExistingEmployeeToProject } from 'store/resources/thunks/joinExistingEmployeeToProject.thunk';

export const AddUserDialog = () => {
  const useStyles = makeStyles((theme) => ({
    dialogPaper: {
      ...theme.component.dialogPaper,
      [theme.breakpoints.down('xs')]: {
        ...theme.component.dialogPaperFullscreen,
      },
    },
    dialogContent: {
      paddingTop: theme.spacing(2),
    },
    dialogActions: {
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
    },
  }));

  const dispatch = useDispatch();

  const classes = useStyles();

  const { t } = useTranslation('common');

  const selectedProject = useSelector(selectSelectedProject);
  const dialogMeta = useSelector(selectAddNewUserDialogMeta);
  const shouldShowRecordDate = Boolean(dialogMeta?.createRecordForUser);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState(null);
  const [taxNumber, setTaxNumber] = useState('');
  const [recordDate, setRecordDate] = useState(moment());

  const [suggestedUser, setSuggestedUser] = useState(null);
  const [joining, setJoining] = useState(false);

  const [projectKey, setProjectKey] = useState(
    selectedProject?.projectKey || ''
  );
  const [businessId, setBusinessId] = useState('');

  const onConfirm = async () => {
    setSuggestedUser(null);
    let phoneNumberToAdd = phoneNumber;
    if (phoneNumberToAdd.charAt(0) !== '+') {
      phoneNumberToAdd = '+' + phoneNumber;
    }
    const result = await dispatch(
      createOrUpdateUser({
        userAttributes: {
          firstName,
          lastName,
          phoneNumber: phoneNumberToAdd,
          ...(dateOfBirth && {
            dateOfBirth: moment(dateOfBirth).format('YYYY-MM-DD'),
          }),
          ...(taxNumber && { taxNumber: taxNumber }),
        },
        projectKeyOfProjectToJoin: projectKey,
        businessIdOfEmployerToJoinWith: businessId,
        projectId: dialogMeta?.projectId || selectedProject?.id,
        createRecordForUser: dialogMeta?.createRecordForUser,
        recordDate: shouldShowRecordDate
          ? moment(recordDate).toISOString()
          : undefined,
      })
    );

    // When the phone belongs to an existing in-business user, the backend
    // returns them as a suggestion instead of creating a duplicate. Offer to
    // add that existing user to the project rather than making a new record.
    if (
      createOrUpdateUser.rejected.match(result) &&
      result.payload?.errorCode === 'user-already-exists' &&
      result.payload?.suggestedUser
    ) {
      setSuggestedUser(result.payload.suggestedUser);
    }
  };

  const onUseExisting = async () => {
    if (!suggestedUser) {
      return;
    }
    setJoining(true);
    const result = await dispatch(
      joinExistingEmployeeToProject({
        projectId: dialogMeta?.projectId || selectedProject?.id,
        userId: suggestedUser.id,
        employerBusinessId:
          suggestedUser.employerBusinessId || businessId || undefined,
        recordDate: shouldShowRecordDate
          ? moment(recordDate).toISOString()
          : undefined,
        selectedUserData: suggestedUser,
      })
    );
    setJoining(false);
    if (joinExistingEmployeeToProject.fulfilled.match(result)) {
      close();
    }
  };

  const close = () => {
    dispatch(closeAddNewUserDialog());
  };

  return (
    <Dialog
      open={true}
      onClose={() => close()}
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>{t('add-user-dialog.add-new-user')}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextInput
              onChange={(event) => setFirstName(event.target.value)}
              value={firstName}
              label={t('add-user-dialog.first-name')}
              type="text"
              id="firstNameInput"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              onChange={(event) => setLastName(event.target.value)}
              value={lastName}
              label={t('add-user-dialog.last-name')}
              type="text"
              id="lastNameInput"
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <TextInput
              onChange={(value) => {
                setPhoneNumber(value);
                setSuggestedUser(null);
              }}
              label={t('add-user-dialog.phone-number')}
              value={phoneNumber}
              type="phone"
              id="phoneNumberInput"
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <DateTimePicker
              type="date"
              onChange={(value) => setDateOfBirth(value)}
              value={dateOfBirth}
              viewMode="days"
              timeFormat={false}
              inputProps={{
                label: t('add-user-dialog.date-of-birth'),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              onChange={(event) => {
                setTaxNumber(event.target.value);
                setSuggestedUser(null);
              }}
              value={taxNumber}
              label={t('add-user-dialog.tax-number')}
              type="text"
              id="taxNumberInput"
              fullWidth
            />
          </Grid>
          {shouldShowRecordDate ? (
            <Grid item xs={12}>
              <DateTimePicker
                type="date"
                onChange={(value) => setRecordDate(value)}
                value={recordDate}
                viewMode="days"
                timeFormat={false}
                inputProps={{
                  label: t('calendar.started_working_at'),
                }}
              />
            </Grid>
          ) : null}
          <h2>{t('add-user-dialog.project-to-add-user-into')}</h2>
          <Grid item xs={12}>
            <TextInput
              value={projectKey}
              onChange={(event) => setProjectKey(event.target.value)}
              label={t('add-user-dialog.project-key-of-project-to-join')}
              type="number"
              id="projectKeyInput"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              value={businessId}
              onChange={(event) => setBusinessId(event.target.value)}
              label={t('add-user-dialog.business-id-of-employer-to-join-with')}
              type="text"
              id="projectKeyInput"
              fullWidth
            />
          </Grid>
          {suggestedUser ? (
            <Grid item xs={12}>
              <div
                style={{
                  border: '1px solid rgba(0,0,0,0.2)',
                  borderRadius: 4,
                  padding: 8,
                  marginTop: 4,
                }}
              >
                <p style={{ margin: '0 0 8px' }}>
                  {suggestedUser.alreadyInProject
                    ? t(
                      'add-user-dialog.user-already-in-project',
                      'Käyttäjä on jo tässä projektissa:'
                    )
                    : t(
                      'add-user-dialog.user-already-exists',
                      'Tällä puhelinnumerolla on jo käyttäjä:'
                    )}{' '}
                  <strong>
                    {[suggestedUser.firstName, suggestedUser.lastName]
                      .filter(Boolean)
                      .join(' ')}
                  </strong>
                  {suggestedUser.taxNumber
                    ? ` (${suggestedUser.taxNumber})`
                    : ''}
                </p>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={joining}
                  onClick={() => onUseExisting()}
                >
                  {suggestedUser.alreadyInProject
                    ? t(
                      'add-user-dialog.continue-to-orientation',
                      'Jatka perehdytykseen'
                    )
                    : t(
                      'add-user-dialog.use-existing-user',
                      'Lisää tämä käyttäjä projektiin'
                    )}
                </Button>
              </div>
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close()}>{t('common.cancel')}</Button>
        <Button
          disabled={!firstName || !lastName || !phoneNumber || !projectKey}
          type="submit"
          color="primary"
          variant="contained"
          onClick={() => onConfirm()}
        >
          {t('add-user-dialog.add-user')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
